/* eslint-disable @typescript-eslint/no-explicit-any */
import {Directive, ElementRef, HostListener, Input, numberAttribute} from '@angular/core';
import {
    AbstractControl,
    AsyncValidator,
    ControlValueAccessor,
    NG_ASYNC_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validators
} from '@angular/forms';
import {debounceTime, first, map, Observable, of} from 'rxjs';
import {PATTERNS} from '../constants';
import {AngleUtilService} from '../../design/services/features/angle/angle-util.service';

/**
 * Short angle format (dd.mmss) input and validation.
 *
 * When input element is NOT focused, angle is displayed in the long format (dd:mm:ss).
 *
 * When input element is focused, the angle is displayed and input using the short format (dd.mmss)
 *
 * Usage: <input type="text" nlrAnglePattern nlrAnglePatternDebounceTime="number">
 *
 * Usage examples:
 *
 * 1. Validate angle after default debounce time:
 *
 *    <input type="text" nlrAnglePattern>
 *
 * 2. Validate angle after custom debounce time:
 *
 *    <input type="text" nlrAnglePattern nlrPatternDebounceTime="500">
 */
@Directive({
    selector: '[nlrAnglePattern]',
    standalone: true,
    providers: [
        {provide: NG_ASYNC_VALIDATORS, useExisting: NlrAnglePatternDirective, multi: true},
        {provide: NG_VALUE_ACCESSOR, useExisting: NlrAnglePatternDirective, multi: true}
    ]
})
export class NlrAnglePatternDirective implements AsyncValidator, ControlValueAccessor {
    private colonPattern = PATTERNS.ANGLE_PATTERN;
    private dotPattern = PATTERNS.SHORT_ANGLE_PATTERN;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onChangeCallback = (value: any) => {};
    onTouchedCallback = () => {};

    @Input({transform: numberAttribute})
    nlrAnglePatternDebounceTime: number = 750;

    constructor(
        private elementRef: ElementRef,
        private angleUtilService: AngleUtilService
    ) {}

    @HostListener('input') onInputChange(): void {
        const input = this.elementRef.nativeElement as HTMLInputElement;

        let value = input.value || '';

        if (value && this.colonPattern.test(value)) {
            value = this.angleUtilService.toDotDms(input.value);
        }

        this.onChangeCallback(value && this.dotPattern.test(value) ? this.angleUtilService.toColonDms(value) : value);
    }

    @HostListener('focus') onFocus(): void {
        const input = this.elementRef.nativeElement as HTMLInputElement;

        let value = input.value || '';

        if (this.colonPattern.test(value)) {
            value = this.angleUtilService.toDotDms(input.value);
        }

        this.elementRef.nativeElement.value = value;

        this.onChangeCallback(value && this.dotPattern.test(value) ? this.angleUtilService.toColonDms(value) : value);
    }

    @HostListener('blur') onBlur(): void {
        const input = this.elementRef.nativeElement as HTMLInputElement;

        let value = input.value || '';

        if (value && !value.includes(':') && !value.includes('.')) {
            value = this.angleUtilService.ddToDms(parseFloat(value));
        }

        if (value && !value.includes(':') && value.includes('.')) {
            value = this.angleUtilService.toColonDms(value);
        }

        this.elementRef.nativeElement.value = value;

        this.onChangeCallback(value);

        this.onTouchedCallback();
    }

    validate(control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
        // When valid, return immediately
        if (this.isValid(control)) {
            return of(null);
        }

        // When invalid, validate again after debounce time has elapsed
        return control.valueChanges.pipe(
            debounceTime(this.nlrAnglePatternDebounceTime),
            map(() => (this.isValid(control) ? null : {nlrAnglePattern: true})),
            first()
        );
    }

    private isValid(control: AbstractControl) {
        return Validators.pattern(this.colonPattern)(control) === null || Validators.pattern(this.dotPattern)(control) === null;
    }

    registerOnChange(fn: any): void {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouchedCallback = fn;
    }

    writeValue(value: any): void {
        this.elementRef.nativeElement.value = value || '';
    }
}
